import type { NavSection } from '../components/index.js'

/**
 * The rail, and the permission each item really needs.
 *
 * **Every one of these was wrong.** The rail asked for `dashboard`,
 * `providers`, `bookings`, `classes`, `events` and `support`, and the API's
 * catalogue is `read`, `read_records`, `edit`, `invite`, `suspend`, `refund`,
 * `money`, `break_glass` and `manage_admins` (and, since decision 5.37,
 * `reply_support`). Six words that are not
 * permissions. Nothing caught it, because the desk was handing itself the same
 * six invented words in a hard-coded array — so the checks agreed with each
 * other and neither agreed with the server.
 *
 * What each item needs is the permission on the route behind it, and nothing
 * else: `GET /providers` is `read`, `GET /transactions` is `money`, the
 * settings group is `manage_admins`.
 */
/**
 * The rail, in three groups.
 *
 * Nine items in one undifferentiated list is the "bland" the client saw when
 * they opened it: nothing said that Members and Payments are the day's work
 * while Settings is a thing you touch monthly.
 *
 * The group names are **never drawn**. They were headings first — "Today",
 * "What we publish", "Running it" — and the client's answer to what they
 * should say instead was nothing at all: a hairline between the groups carries
 * the grouping, and three more pieces of text do not. They stay here as the
 * key that decides where the lines fall, which is the one thing a heading was
 * ever good for.
 */
export const RAIL: NavSection[] = [
  { key: 'overview', label: 'Overview', permission: 'read', icon: 'overview', group: 'Today' },
  /**
   * The people the platform is for.
   *
   * Added 2026-09-21, at the client's asking. It sits above Providers because
   * that is the order the product itself puts them in — the User is the
   * customer and the Provider is who they meet — and because four endpoints
   * had been waiting for it since the admin surface was written.
   *
   * `read` opens the table; the record behind it is `read_records`, and the
   * health record inside that is `break_glass`, which no preset carries.
   */
  { key: 'members', label: 'Members', permission: 'read', icon: 'members', group: 'Today' },
  { key: 'providers', label: 'Providers', permission: 'read', icon: 'providers', group: 'Today' },
  { key: 'bookings', label: 'Bookings', permission: 'read', icon: 'bookings', group: 'Today' },
  /* Payments is gone from the rail for Support entirely, "because Support has
     no reason to see money" — which is what `money` rather than `read` does. */
  { key: 'payments', label: 'Payments', permission: 'money', icon: 'payments', group: 'Today' },
  { key: 'classes', label: 'Classes', permission: 'read', icon: 'classes', group: 'What we publish' },
  { key: 'events', label: 'Events', permission: 'read', icon: 'events', group: 'What we publish' },
  { key: 'support', label: 'Support', permission: 'read', icon: 'support', group: 'Running it' },
  /**
   * 9.13, the activity log (client, 2026-10-03): everything anybody did. `read_records`,
   * the permission behind a member's or a professional's record, because it is their
   * history; what the team did among itself is narrowed to `manage_admins` row by row
   * on the server.
   */
  { key: 'activity', label: 'Activity', permission: 'read_records', icon: 'activity', group: 'Running it' },
  { key: 'settings', label: 'Settings', permission: 'manage_admins', icon: 'settings', group: 'Running it' },
]

/**
 * 9.8's line, for a role that can open everything and change nothing.
 *
 * The board's own words. It sits above the tables rather than inside one,
 * because it is a fact about the role and not about the page — Support sees it
 * on Providers, on Bookings and on Classes alike.
 */
export const READ_ONLY_SAID =
  'You can open and read every record, but cannot invite, pause or refund. Actions your role cannot take are greyed out rather than hidden, so you can tell a caller what exists and who to ask.'

/**
 * The three things board 9.8 says Support cannot do.
 *
 * The line above the tables shows when somebody holds none of them, which is
 * the board's own sentence rather than a paraphrase of it. The Support preset
 * is `read`, `read_records` and `reply_support` — migration 0031 took `edit`
 * off it and gave it the permission that opens a record, because *"the role is
 * what the screen says it is"*, and 0049 gave it the inbox reply (decision
 * 5.37), which is answering people rather than acting on a record — so either list reaches the same answer for the three seeded
 * roles. This one says why, in the board's words, and stays right if somebody
 * invents a fourth.
 */
export const ACTING_PERMISSIONS = ['invite', 'suspend', 'refund'] as const
